import { v4 as uuidv4 } from 'uuid';
import { getContext } from 'configs/context';
import { injectServiceFetchAI } from '@picsart/editor-lib';

import { AIBaseUrl } from 'configs/constants';

interface IFetchOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  headers?: Record<string, string>;
  isFormData?: boolean;
  method?: string;
  timeout?: number;
}

const X_TOUCHPOINT = 'x-touchpoint';

enum AiToolsTouchPoint {
  Effects = 'ai_effects',
}

const fetchWithTimeout = (url: string, options: IFetchOptions): Promise<Response> => {
  if (options.signal) throw new Error('Signal not supported in timeoutable fetch');

  const controller = new AbortController();
  const { signal } = controller;

  return getContext().utils.api.fetch(url, { signal, ...options });
};

class FetchAI {
  public static uploadImage = async (opts: IFetchOptions) => {
    const options = { ...opts };
    options.headers = options.headers || {};
    options.isFormData = true;
    options.timeout = 120000;

    const photoId = uuidv4();

    return FetchAI.fetch(`/photos/${photoId}`, {
      ...options,
      method: 'POST',
    });
  };

  public static magicEffect = (opts: IFetchOptions) => {
    const options = { ...opts };
    options.headers = options.headers || {};
    options.headers[X_TOUCHPOINT] = AiToolsTouchPoint.Effects;

    return FetchAI.fetch(`/effects/${options.body.effect}/${options.body.sid}`, {
      ...options,
      method: 'POST',
    });
  };

  public static cartoonEffect = (opts: IFetchOptions, sid: string, model = 'Cartoon1') => {
    const options = { ...opts };
    options.headers = options.headers || {};
    options.headers[X_TOUCHPOINT] = AiToolsTouchPoint.Effects;

    return FetchAI.fetch(`/cartoon-effects/v1/?photo_id=${sid}&model=${model}`, {
      ...options,
      method: 'GET',
    });
  };

  private static async fetch(path: string, options?: IFetchOptions) {
    const { headers, method, body, isFormData, timeout = 15000 } = options as IFetchOptions;

    const requestOptions: IFetchOptions = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        platform: 'web',
        ...headers,
      },
      method,
      timeout,
    };

    if (requestOptions.headers && isFormData) {
      delete requestOptions.headers['Content-Type'];
    }

    if (body) {
      requestOptions.body = isFormData ? body : JSON.stringify(body);
    }

    const response = await fetchWithTimeout(`${AIBaseUrl}${path}`, requestOptions);

    if (response.status >= 200 && response.status < 300) {
      try {
        return await response.json();
      } catch {
        return { errorMessage: 'Server error', status: response.status };
      }
    } else if (response.status === 401) {
      return {};
    } else {
      const errorMessage = (await response.text()) || 'Server error';
      return { errorMessage, status: response.status };
    }
  }
}

injectServiceFetchAI(FetchAI);
